import { ReactElement, ReactNode } from 'react';
import { GoogleReCaptchaProvider as GRP } from 'react-google-recaptcha-v3';
import { GoogleOAuthProvider as GOP } from '@react-oauth/google';
import AppleSign from 'react-apple-signin-auth';

interface IGoogleReCaptchaProviderProps {
  reCaptchaKey: string;
  language?: string;
  useRecaptchaNet?: boolean;
  useEnterprise?: boolean;
  scriptProps?: {
    nonce?: string;
    defer?: boolean;
    async?: boolean;
    appendTo?: 'head' | 'body';
    id?: string;
    onLoadCallbackName?: string;
  };
  container?: {
    element?: string | HTMLElement;
    parameters: {
      badge?: 'inline' | 'bottomleft' | 'bottomright';
      theme?: 'dark' | 'light';
      tabindex?: number;
      callback?: () => void;
      expiredCallback?: () => void;
      errorCallback?: () => void;
    };
  };
  children: ReactNode;
}

interface UseLoadGsiScriptOptions {
  /**
   * Nonce applied to GSI script tag. Propagates to GSI's inline style tag
   */
  nonce?: string;
  /**
   * Callback fires on load [gsi](https://accounts.google.com/gsi/client) script success
   */
  onScriptLoadSuccess?: () => void;
  /**
   * Callback fires on load [gsi](https://accounts.google.com/gsi/client) script failure
   */
  onScriptLoadError?: () => void;
}

interface GoogleOAuthProviderProps extends UseLoadGsiScriptOptions {
  clientId: string;
  children: ReactNode;
}

export function GoogleReCaptchaProvider(props: IGoogleReCaptchaProviderProps): ReactElement {
  return GRP(props) as any;
}

export function GoogleOAuthProvider(props: GoogleOAuthProviderProps): ReactElement {
  return GOP(props) as any;
}

export type AppleAuthOptions = {
  /** Client ID - eg: 'com.example.com' */
  clientId: string;
  /** Requested scopes, seperated by spaces - eg: 'email name' */
  scope: string;
  /** Apple's redirectURI - must be one of the URIs you added to the serviceID - the undocumented trick in apple docs is that you should call auth from a page that is listed as a redirectURI, localhost fails */
  redirectURI: string;
  /** State string that is returned with the apple response */
  state?: string;
  /** Nonce */
  nonce?: string;
  /** Uses popup auth instead of redirection */
  usePopup?: boolean;
};

export type AppleSignInButtonProps = {
  authOptions: AppleAuthOptions;
  /** Called upon signin success in case authOptions.usePopup = true -- which means auth is handled client side */
  onSuccess: Function;
  /** Called upon signin error */
  onError: Function;
  /** Skips loading the apple script if true */
  skipScript?: boolean;
  /** Apple image props */
  iconProps?: Object;
  /** render function - called with all props - can be used to fully customize the UI by rendering your own component  */
  render?: Function;
  /** UI type */
  uiType: 'light' | 'dark';
  /** className */
  className?: string | null | undefined;
  /** prevents rendering of default styles */
  noDefaultStyle?: boolean;
  /** Allows to change the button's children, eg: for changing the button text */
  buttonExtraChildren?: string | ReactNode;
  /** Rest is spread on the root button component */
};

export function AppleSignin(props: AppleSignInButtonProps): ReactElement {
  return AppleSign(props as any) as any;
}
